import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import LoginPage from './components/Auth/LoginPage';
import SetPasswordPage from './components/Auth/SetPasswordPage';
import ForgotPasswordPage from './components/Auth/ForgotPasswordPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import ChangePasswordPage from './components/Auth/ChangePasswordPage';
import SetupPassword from './pages/SetupPasswordPage/SetupPassword';
import MainLayout from './components/Layout/MainLayout';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import DoctorDashboard from './pages/Doctor/DoctorDashboard';
import MarketExecutiveDashboard from './pages/MarketExecutive/MarketExecutiveDashboard';
import HealthMonitorPage from './pages/HealthMonitor/HealthMonitorPage';
import PatientsPage from './pages/Patients/PatientsPage';
import AppointmentsPage from './pages/Appointments/AppointmentsPage';
import MessagesPage from './pages/Messages/MessagesPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import ProfilePage from './pages/Profile/ProfilePage';
import DoctorsPage from './pages/Doctors/DoctorsPage';
import MarketExecutivesPage from './pages/MarketExecutives/MarketExecutivesPage';
import CMSPage from './pages/CMS/CMSPage';

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
  },
});

// Dashboard Router Component
function DashboardRouter() {
  return (
    <Routes>
      <Route path="/dashboard" element={<DashboardSelector />} />
      <Route
        path="/doctors"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'marketinguser']}>
            <DoctorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/market-executives"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <MarketExecutivesPage />
          </ProtectedRoute>
        }
      />
      <Route path="/appointments" element={<AppointmentsPage />} />
      <Route
        path="/patients"
        element={
          <ProtectedRoute allowedRoles={['superadmin', 'doctor', 'marketinguser']}>
            <PatientsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/health-monitor" element={<HealthMonitorPage />} />
      <Route
        path="/cms"
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <CMSPage />
          </ProtectedRoute>
        }
      />
      <Route path="/messages" element={<MessagesPage />} />

      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Dashboard Selector Component
function DashboardSelector() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'superadmin') {
    return <SuperAdminDashboard />;
  } else if (user.role === 'doctor') {
    return <DoctorDashboard />;
  } else if (user.role === 'marketinguser') {
    return <MarketExecutiveDashboard />;
  }

  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationsProvider>
          <Router>
            <Routes>
              {/* Login Route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Set Password Route */}
              <Route path="/setpassword/:userId" element={<SetPasswordPage />} />

              {/* Setup Password Route (via email link) */}
              <Route path="/setup-password" element={<SetupPassword />} />

              {/* Forgot Password Route */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              {/* Reset Password Route */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Protected Routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DashboardRouter />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </NotificationsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
